import { useQuery } from '@tanstack/react-query';

/**
 * The person's NADC apps, asked of the portal (2026-10-09).
 *
 * Until 2026-10-09 the shared NADC bar (nadc1.net's navbar.js, loaded by
 * index.html) drew the app launcher and the user menu above DemoOps. Michael
 * dropped the bar to save screen space — "I am thinking about dropping the
 * common top nav bar to save even more screen size" — so DemoOps now asks the
 * portal the same question the bar did and draws the answer in its own header
 * (NadcAppsLauncher).
 *
 * Mirrors navbar.js on purpose: the same request with the portal session
 * cookie, ONE retry after 2 s, then the bar's two safe links (Portal, Help
 * Desk). It never shows an error and never logs — a broken launcher must not
 * get in the way of the app, and the payload names the person. The launcher
 * mounts with the shell, so the answer is usually in before the menu opens;
 * until then (and for good, if the portal cannot be asked) the safe links
 * stand in.
 *
 * On preview.nadc1.net the request ALWAYS fails: the portal grants CORS to
 * projectmanagement.nadc1.net only. The preview therefore shows the safe
 * links — expected, not a portal bug. The browser itself reports the refused
 * request in the console; nothing here adds to it.
 */

export const NADC_NAVIGATION_URL = 'https://nadc1.net/api/navigation';

export const nadcNavigationKey = ['nadc-navigation'] as const;

export interface NadcAppLink {
  name: string;
  url: string;
  host: string;
}

export interface NadcPortalUser {
  /** The portal's display_name, else its username; null when it sent neither. */
  name: string | null;
  department: string | null;
  isPortalAdmin: boolean;
}

export interface NadcNavigation {
  /** null while the safe links stand in: not asked yet, failed, or anonymous. */
  user: NadcPortalUser | null;
  links: NadcAppLink[];
  /** Open help desk tickets; 0 when unknown. */
  openTickets: number;
}

/** navbar.js's SAFE_LINKS: what everyone may open, portal answer or not. */
export const SAFE_LINKS: NadcAppLink[] = [
  { name: 'Portal', url: 'https://nadc1.net', host: 'nadc1.net' },
  { name: 'Help Desk', url: 'https://helpdesk.nadc1.net', host: 'helpdesk.nadc1.net' },
];

const SAFE_NAVIGATION: NadcNavigation = { user: null, links: SAFE_LINKS, openTickets: 0 };

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * One link from the answer, or null. Only http(s) addresses survive: React
 * renders whatever href it is given, so a `javascript:` URL in the portal's
 * apps table would otherwise become a live link here (navbar.js's safeHref
 * guarded the same way).
 */
function toLink(raw: unknown): NadcAppLink | null {
  if (!raw || typeof raw !== 'object') return null;
  const l = raw as Record<string, unknown>;
  const name = text(l.name);
  const url = text(l.url);
  if (!name || !url || !/^https?:\/\//i.test(url)) return null;
  let host = text(l.host);
  if (!host) {
    try { host = new URL(url).host; } catch { return null; }
  }
  return { name, url, host };
}

/**
 * The portal's answer, or null when it is not one we can use. navbar.js's
 * test, kept: an answer with no links is a failure (and is retried); an
 * answer for nobody (user null) is a real answer that the hook turns into
 * the safe links.
 */
function parseNavigation(payload: unknown): NadcNavigation | null {
  if (!payload || typeof payload !== 'object') return null;
  const nav = payload as Record<string, unknown>;
  if (!Array.isArray(nav.links)) return null;
  const links = nav.links.map(toLink).filter((l): l is NadcAppLink => l !== null);
  if (links.length === 0) return null;

  let user: NadcPortalUser | null = null;
  if (nav.user && typeof nav.user === 'object') {
    const u = nav.user as Record<string, unknown>;
    user = {
      name: text(u.display_name) ?? text(u.username),
      department: text(u.department),
      isPortalAdmin: u.is_portal_admin === true,
    };
  }
  const tickets = nav.ticket_summary as Record<string, unknown> | null | undefined;
  const open = tickets && typeof tickets === 'object' ? tickets.open : 0;

  return {
    user,
    links,
    openTickets: typeof open === 'number' && Number.isFinite(open) && open > 0 ? open : 0,
  };
}

async function fetchNadcNavigation(): Promise<NadcNavigation> {
  // The portal's session cookie rides along (credentials: 'include'); the
  // portal answers projectmanagement.nadc1.net with allow-credentials. Plain
  // fetch, not the api client: that one adds DemoOps' CSRF and correlation
  // headers and treats a 401 as DemoOps' own session ending.
  const response = await fetch(NADC_NAVIGATION_URL, { credentials: 'include', cache: 'no-store' });
  if (!response.ok) throw new Error(`NADC navigation answered ${response.status}`);
  const nav = parseNavigation(await response.json());
  if (!nav) throw new Error('NADC navigation answer was not usable');
  return nav;
}

export function useNadcNavigation(): NadcNavigation {
  const { data } = useQuery({
    queryKey: nadcNavigationKey,
    queryFn: fetchNadcNavigation,
    // The bar asked on every page load; the shell stays up all day, so one
    // answer serves the session and a remount within 10 minutes reuses it.
    staleTime: 10 * 60_000,
    // navbar.js: exactly ONE recovery attempt, 2 s later, then the safe links.
    retry: 1,
    retryDelay: 2_000,
  });
  return data?.user ? data : SAFE_NAVIGATION;
}
